import { faTrash } from '@fortawesome/pro-regular-svg-icons';
import { faTrash as faTrashSolid } from '@fortawesome/pro-solid-svg-icons';
import {
    IUserCorporationChild,
    IUserResponse,
} from '@kcftech/prometheustypescriptapiclient';
import { HoverIconButton } from 'components/button/HoverIconButton';
import { MemoizedTable } from 'components/data_table/Table';
import { TableColumn } from 'components/data_table/types';
import { ActionMenuTableCell } from 'components/menu/ActionMenuTableCell';
import { usePermissions } from 'contexts/PermissionsContext';
import { StyledCell } from 'pages/Settings/Users/shared';
import { CorporationUserRoleCell } from 'pages/Settings/Users/table_cells/CorporationUserRoleCell';
import { useMemo } from 'react';
import { DebugValue } from 'shared/debug/DebugValue';
import { useThemeValues } from 'styles/theme';

export type CorporationsForUserTableProps = Readonly<{
    selectedUserInfo: IUserResponse;
    onDeleteCorporationForUserClick: (
        userCorporationChild: IUserCorporationChild
    ) => void;
    onChange: () => void;
}>;

export function CorporationsForUserTable({
    selectedUserInfo,
    onDeleteCorporationForUserClick,
    onChange,
}: CorporationsForUserTableProps) {
    const { secondaryText, deleteButton } = useThemeValues();
    const { canEditUserCorporationAssignment } = usePermissions();

    const columns = useMemo<TableColumn<IUserCorporationChild>[]>(
        () => [
            {
                id: 'CorporationName',
                header: () => 'Corporation Name',
                value: ({ rowData }) => (
                    <StyledCell>
                        {rowData.CorporationName}
                        <DebugValue value={rowData} description="CorporationToUser" />
                    </StyledCell>
                ),
                primary: true,
                sortOn: 'CorporationName',
            },
            {
                id: 'CorporationRole',
                header: () => 'Corporation Role',
                value: ({ rowData }) => (
                    <CorporationUserRoleCell
                        rowData={selectedUserInfo}
                        corporationId={rowData.CorporationId}
                        onChange={onChange}
                        disabled={
                            !canEditUserCorporationAssignment(rowData.CorporationId)
                        }
                    />
                ),
                primary: true,
                sortOn: 'CorporationUserRole',
            },
            {
                id: 'actions',
                header: () => 'Actions',
                value: ({ rowData }) => {
                    const canEdit = canEditUserCorporationAssignment(
                        rowData.CorporationId
                    );
                    return (
                        <ActionMenuTableCell>
                            <HoverIconButton
                                className="flex-row no-wrap"
                                icon={faTrash}
                                hoverIcon={faTrashSolid}
                                color={secondaryText}
                                hoverColor={deleteButton}
                                onClick={() => onDeleteCorporationForUserClick(rowData)}
                                title={
                                    canEdit
                                        ? 'Delete Corporation assignment'
                                        : 'Insufficient permissions to delete'
                                }
                                disabled={!canEdit}
                            />
                        </ActionMenuTableCell>
                    );
                },
            },
        ],
        [
            canEditUserCorporationAssignment,
            deleteButton,
            onChange,
            onDeleteCorporationForUserClick,
            secondaryText,
            selectedUserInfo,
        ]
    );

    return (
        <>
            {selectedUserInfo.Corporations.length > 0 ? (
                <MemoizedTable
                    data={selectedUserInfo.Corporations}
                    rowKey={({ CorporationId }: IUserCorporationChild) => CorporationId}
                    columns={columns}
                />
            ) : (
                <span>No corporations have been assigned to this user yet.</span>
            )}
        </>
    );
}
